import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PatientProfileSummary from './components/PatientProfileSummary';
import QuotationSummary from './components/QuotationSummary';
import MedicalDocuments from './components/MedicalDocuments';
import FinalConfirmation from './components/FinalConfirmation';
import './style.css';
import { useGet } from '@/api/hooks/useGet';
import { GetAdmissionDetails, SubmitAdmission } from '@/api/endpoints/Requests';
import ActionBtn from '@/components/ActionBtn';
import moment from 'moment';
import { Icon } from '@iconify/react/dist/iconify.js';
import { errorToast, successToast } from '@/utils/toasters';

const Admission = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const requestId = state?.requestId;

    const { result: admissionDetails, isPending } = useGet({
        endPoint: GetAdmissionDetails,
        depArr: [],
        allowFetch: !!requestId,
        params: [requestId]
    });

    const handleBack = () => {
        navigate('/panel/provider/requests/medical');
    };

    const handleSubmit = () => {
        setLoading(true);
        SubmitAdmission(requestId).then((res) => {
            successToast("Admission submitted successfully");
            navigate('/panel/provider/requests/medical');
            setLoading(false);
        }).catch((err) => {
            errorToast("Error submitting admission");
            setLoading(false);
        });
    };

    return (
        <div className="admission-container">
            {/* Patient Identity Header */}
            <div className="admission-patient-header">
                <div className="admission-patient-header-left">
                    <div className="admission-patient-identity-badge">
                        <span className="admission-patient-identity-text">{t('patient-identity-token')}</span>
                        <span className="admission-verified-badge">{t('verified')}</span>
                    </div>
                    <h1 className="admission-patient-name-title">{admissionDetails?.patientData?.name }</h1>
                    <div className="admission-patient-meta">
                        <span className="admission-patient-meta-item">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
                            </svg>
                            {admissionDetails?.patientData?.age}
                        </span>
                        <div className="admission-group">
                            <span className="admission-patient-meta-divider">*</span>
                            <span className="admission-patient-meta-item">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                                    <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                                {state?.requestId}
                            </span>
                        </div>
                        <div className="admission-group">

                            <span className="admission-patient-meta-divider">*</span>
                            <span className="admission-patient-meta-item">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px' }}>
                                    <path d="M12 2a5 5 0 015 5v3a5 5 0 01-10 0V7a5 5 0 015-5z" stroke="currentColor" strokeWidth="2" />
                                    <path d="M12 14c-5 0-8 3-8 6v2h16v-2c0-3-3-6-8-6z" stroke="currentColor" strokeWidth="2" />
                                </svg>
                                {admissionDetails?.patientData?.gender}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="admission-patient-header-right">
                    <div className="admission-status-badge-orange">
                        {t('awaiting-procedure-start')}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="admission-content">
                <div className="admission-content-left">
                    <PatientProfileSummary admissionDetails={admissionDetails} />
                    <QuotationSummary admissionDetails={admissionDetails} />
                </div>

                <div className="admission-content-right">
                    <MedicalDocuments admissionDetails={admissionDetails} />
                    <FinalConfirmation onSubmit={handleSubmit} isLoading={isPending} />
                </div>
            </div>
            <div className="admission-footer-submission">
                <div onClick={handleBack} className="admission-footer-submission-back-btn">
                   <Icon icon={"fluent-mdl2:back"} />
                    {t("Back to Selection")}
                </div>
                <div className="admission-footer-submission-right">
                    <div className="admission-footer-submission-time-container">
                    <span>{t("entry-time")}</span>
                        <div className="admission-footer-submission-time">{`${moment().format("MMMM DD,YYYY")} . ${moment().format("hh:mm a").toUpperCase()}`}</div>
                    </div>
                    <br/>
                    <ActionBtn className="admission-footer-submission-btn" icon={"mdi:motion-play"} onApply={handleSubmit}  title={loading ?"loading" :"Admission"} />
                </div>
            </div>
        </div>
    );
};

export default Admission;

